import type { MeasurementAnswerInput } from '@/src/dal/measurement-dal'
import type { QuestionDefinition } from '@/src/dal/session-dal'
import { useCallback } from 'react'
import { FlatList, Pressable, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Head from 'expo-router/head'
import { StatusBar } from 'expo-status-bar'
import { Button } from '@/src/components/ui/Button'
import { Icon, symbolName } from '@/src/components/ui/Icon'
import { colors } from '@/src/theme'
import { body3, body4, label1, label2 } from '@/src/theme/typography'
import { useTranslation } from 'react-i18next'

const NON_SCALING_TEXT_PROPS = {
  allowFontScaling: false,
  maxFontSizeMultiplier: 1,
} as const

const CONTROL_BORDER_RADIUS = 8
const QUESTION_GAP = 12
const OPTION_GAP = 10
const OPTION_HEIGHT = 44
const RATING_BUTTON_HEIGHT = 40
const RATING_GAP = 8

interface VoiceAssessmentDynamicQuestionsStepProps {
  stepHeaderLabel: string
  questions: QuestionDefinition[]
  answers: MeasurementAnswerInput[]
  onSingleChoiceChange: (questionId: number, choiceIndex: number) => void
  onToggleMultipleChoice: (questionId: number, choiceIndex: number) => void
  onNext: () => void
  onSkip: () => void
  isSubmitting: boolean
  progressCurrentStep: number
  progressTotalSteps: number
}

function getChoiceIndices(
  answers: MeasurementAnswerInput[],
  questionId: number,
): number[] {
  return (
    answers.find((answer) => answer.questionId === questionId)?.choiceIndices ??
    []
  )
}

function canContinue(
  questions: QuestionDefinition[],
  answers: MeasurementAnswerInput[],
): boolean {
  return questions.every((question) => {
    if (question.questionType !== 'SingleChoice') {
      return true
    }
    return getChoiceIndices(answers, question.id).length > 0
  })
}

function SingleChoiceQuestionSection({
  title,
  options,
  minLabel,
  maxLabel,
  selectedChoiceIndex,
  onChange,
}: {
  title: string
  options: string[]
  minLabel?: string | null
  maxLabel?: string | null
  selectedChoiceIndex: number | null
  onChange: (choiceIndex: number) => void
}) {
  const lowLabel = minLabel ?? options[0] ?? ''
  const highLabel = maxLabel ?? options[options.length - 1] ?? ''

  return (
    <View style={{ gap: 8 }}>
      <Text
        {...NON_SCALING_TEXT_PROPS}
        style={[label1, { color: colors.textHeader }]}
      >
        {title}
      </Text>
      <View className="flex-row" style={{ gap: RATING_GAP }}>
        {options.map((_option, choiceIndex) => {
          const isSelected = selectedChoiceIndex === choiceIndex
          return (
            <Pressable
              key={choiceIndex}
              onPress={() => onChange(choiceIndex)}
              className="flex-1 rounded-lg border items-center justify-center"
              style={{
                height: RATING_BUTTON_HEIGHT,
                borderRadius: CONTROL_BORDER_RADIUS,
                borderColor: isSelected ? colors.primary : colors.border,
                backgroundColor: isSelected
                  ? colors.secondary700
                  : colors.background,
              }}
            >
              <Text
                {...NON_SCALING_TEXT_PROPS}
                style={[
                  label1,
                  {
                    color: isSelected ? colors.textHeader : colors.textBody,
                  },
                ]}
              >
                {choiceIndex + 1}
              </Text>
            </Pressable>
          )
        })}
      </View>
      <View className="flex-row justify-between">
        <Text
          {...NON_SCALING_TEXT_PROPS}
          style={[body3, { color: colors.textBody }]}
        >
          {lowLabel}
        </Text>
        <Text
          {...NON_SCALING_TEXT_PROPS}
          style={[body3, { color: colors.textBody, textAlign: 'right' }]}
        >
          {highLabel}
        </Text>
      </View>
    </View>
  )
}

function MultipleChoiceQuestionSection({
  questionId,
  title,
  options,
  selectedChoiceIndices,
  onToggleMultipleChoice,
  instructionText,
}: {
  questionId: number
  title: string
  options: string[]
  selectedChoiceIndices: Set<number>
  onToggleMultipleChoice: (questionId: number, choiceIndex: number) => void
  instructionText: string
}) {
  return (
    <View style={{ gap: QUESTION_GAP, width: '100%' }}>
      <Text
        {...NON_SCALING_TEXT_PROPS}
        style={[label1, { color: colors.textHeader }]}
      >
        {title}
      </Text>
      <Text
        {...NON_SCALING_TEXT_PROPS}
        style={[body4, { color: colors.textBody }]}
      >
        {instructionText}
      </Text>
      <View style={{ gap: OPTION_GAP }}>
        {options.map((option, choiceIndex) => {
          const isSelected = selectedChoiceIndices.has(choiceIndex)

          return (
            <Pressable
              key={`${questionId}-${choiceIndex}`}
              onPress={() => onToggleMultipleChoice(questionId, choiceIndex)}
              className="flex-row items-center justify-between rounded-lg border px-4"
              style={{
                height: OPTION_HEIGHT,
                width: '100%',
                borderRadius: CONTROL_BORDER_RADIUS,
                borderColor: isSelected ? colors.primary : colors.border,
                backgroundColor: isSelected
                  ? colors.secondary700
                  : colors.background,
              }}
            >
              <Text
                {...NON_SCALING_TEXT_PROPS}
                style={[label1, { color: colors.textHeader }]}
              >
                {option}
              </Text>
              <View
                className="items-center justify-center border"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  backgroundColor: isSelected ? colors.primary : 'transparent',
                  borderColor: isSelected ? colors.primary : colors.border,
                  borderWidth: 1.5,
                }}
              >
                {isSelected ? (
                  <Icon
                    name={symbolName('checkmark', 'check')}
                    size={16}
                    color={colors.primaryForeground}
                  />
                ) : null}
              </View>
            </Pressable>
          )
        })}
      </View>
    </View>
  )
}

function VoiceAssessmentQuestionListItem({
  answers,
  instructionText,
  onSingleChoiceChange,
  onToggleMultipleChoice,
  question,
  questionIndex,
}: {
  answers: MeasurementAnswerInput[]
  instructionText: string
  onSingleChoiceChange: (questionId: number, choiceIndex: number) => void
  onToggleMultipleChoice: (questionId: number, choiceIndex: number) => void
  question: QuestionDefinition
  questionIndex: number
}) {
  const selectedChoiceIndices = new Set(getChoiceIndices(answers, question.id))
  const title = question.title?.trim() || `Question ${questionIndex + 1}`

  if (question.questionType === 'SingleChoice') {
    return (
      <SingleChoiceQuestionSection
        title={title}
        options={question.options}
        minLabel={question.minLabel}
        maxLabel={question.maxLabel}
        selectedChoiceIndex={getChoiceIndices(answers, question.id)[0] ?? null}
        onChange={(choiceIndex) =>
          onSingleChoiceChange(question.id, choiceIndex)
        }
      />
    )
  }

  return (
    <MultipleChoiceQuestionSection
      questionId={question.id}
      title={title}
      options={question.options}
      selectedChoiceIndices={selectedChoiceIndices}
      onToggleMultipleChoice={onToggleMultipleChoice}
      instructionText={instructionText}
    />
  )
}

export function VoiceAssessmentDynamicQuestionsStep({
  stepHeaderLabel,
  questions,
  answers,
  onSingleChoiceChange,
  onToggleMultipleChoice,
  onNext,
  onSkip,
  isSubmitting,
  progressCurrentStep,
  progressTotalSteps,
}: VoiceAssessmentDynamicQuestionsStepProps) {
  const { t } = useTranslation(['onboarding', 'common'])
  const insets = useSafeAreaInsets()
  const footerPaddingBottom = Math.max(insets.bottom, 8)
  const assessmentProgressSteps = Array.from(
    { length: progressTotalSteps },
    (_, index) => index + 1,
  )
  const isNextDisabled = isSubmitting || !canContinue(questions, answers)
  const instructionText = t(
    'onboarding:reflection.tension_location_instruction',
  )
  const renderQuestionItem = useCallback(
    ({
      item: question,
      index: questionIndex,
    }: {
      item: QuestionDefinition
      index: number
    }) => (
      <VoiceAssessmentQuestionListItem
        answers={answers}
        instructionText={instructionText}
        onSingleChoiceChange={onSingleChoiceChange}
        onToggleMultipleChoice={onToggleMultipleChoice}
        question={question}
        questionIndex={questionIndex}
      />
    ),
    [answers, instructionText, onSingleChoiceChange, onToggleMultipleChoice],
  )

  return (
    <View
      className="bg-background-primary flex-1"
      style={{
        flex: 1,
        backgroundColor: colors.backgroundPrimary,
        paddingTop: insets.top,
      }}
    >
      <Head>
        <title>{t('common:page_title_onboarding')}</title>
      </Head>
      <StatusBar style="dark" />
      <View className="px-6 pt-6 pb-6">
        <View className="flex-row items-center justify-between mb-2 gap-2">
          <Text
            {...NON_SCALING_TEXT_PROPS}
            numberOfLines={2}
            style={[label2, { color: colors.textMuted, flex: 1 }]}
          >
            {stepHeaderLabel}
          </Text>
          <Pressable onPress={onSkip} className="active:opacity-60">
            <Text
              {...NON_SCALING_TEXT_PROPS}
              style={[label2, { color: colors.textBody }]}
            >
              {t('onboarding:voice_assessment.skip_header')}
            </Text>
          </Pressable>
        </View>
        <View className="flex-row items-center gap-2">
          {assessmentProgressSteps.map((stepNumber) => (
            <View
              key={`assessment-progress-${stepNumber}`}
              className={`flex-1 rounded-full ${stepNumber <= progressCurrentStep ? 'bg-primary' : 'bg-border'}`}
              style={{ height: 9 }}
            />
          ))}
        </View>
      </View>

      <FlatList
        data={questions}
        keyExtractor={(question) => String(question.id)}
        contentInsetAdjustmentBehavior="automatic"
        style={{ flex: 1 }}
        contentContainerStyle={{
          paddingHorizontal: 24,
          paddingTop: 8,
          paddingBottom: 24,
          width: '100%',
        }}
        ItemSeparatorComponent={() => <View style={{ height: 26 }} />}
        showsVerticalScrollIndicator={false}
        renderItem={renderQuestionItem}
      />

      <View
        style={{ paddingHorizontal: 24, paddingBottom: footerPaddingBottom }}
      >
        <Button
          label={t('onboarding:post_payment.step_2_cta_next')}
          onPress={onNext}
          variant="primary"
          size="md"
          loading={isSubmitting}
          disabled={isNextDisabled}
          containerClassName="w-full"
        />
      </View>
    </View>
  )
}
